import { useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Eye, EyeOff, Check, ArrowRight, KeyRound } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";

const API = process.env.REACT_APP_BACKEND_URL;

function formatDetail(detail) {
  if (detail == null) return "Ocorreu um erro. Tente novamente.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return detail?.msg || String(detail);
}

export default function ResetPassword() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [pwd, setPwd] = useState("");
  const [pwd2, setPwd2] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (pwd.length < 6) return setError("A senha deve ter ao menos 6 caracteres");
    if (pwd !== pwd2) return setError("As senhas não coincidem");
    setLoading(true);
    try {
      await axios.post(`${API}/api/auth/reset-password`, { token, new_password: pwd }, { withCredentials: true });
      setDone(true);
      toast.success("Senha redefinida com sucesso!");
      setTimeout(() => navigate("/login"), 2500);
    } catch (err) {
      setError(formatDetail(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-6" data-testid="reset-password-page">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-sm border border-border p-8">
        <div className="flex items-center gap-3 mb-6">
          <div className="h-12 w-12 rounded-xl bg-brand-turquoise/10 flex items-center justify-center">
            <KeyRound className="h-6 w-6 text-brand-turquoise" />
          </div>
          <div>
            <p className="font-heading font-bold text-brand-turquoise text-lg leading-tight">Anjos de Patas</p>
            <p className="text-[10px] uppercase tracking-widest text-brand-lilac font-semibold">Redefinir senha</p>
          </div>
        </div>

        {done ? (
          <div className="text-center py-4" data-testid="reset-success">
            <div className="mx-auto h-16 w-16 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center mb-4">
              <Check className="h-8 w-8" />
            </div>
            <h2 className="font-heading text-xl font-bold text-slate-900">Senha redefinida!</h2>
            <p className="mt-2 text-sm text-muted-foreground">Redirecionando para a tela de login…</p>
            <Link to="/login" className="mt-4 inline-block text-sm text-brand-turquoise hover:underline">Ir para o login agora</Link>
          </div>
        ) : (
          <>
            <h2 className="font-heading text-2xl font-bold text-slate-900">Criar nova senha</h2>
            <p className="mt-1 text-sm text-muted-foreground">Escolha uma senha com ao menos 6 caracteres.</p>

            <form onSubmit={submit} className="mt-6 space-y-4">
              {error && (
                <Alert variant="destructive" data-testid="reset-error" className="border-destructive/30 bg-destructive/5">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              <div className="space-y-2">
                <Label htmlFor="pwd">Nova senha</Label>
                <div className="relative">
                  <Input id="pwd" data-testid="reset-pwd-input" type={showPwd ? "text" : "password"} required minLength={6}
                    value={pwd} onChange={(e) => setPwd(e.target.value)} className="h-11 rounded-lg pr-11" placeholder="••••••••" />
                  <button type="button" onClick={() => setShowPwd((v) => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-slate-700">
                    {showPwd ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="pwd2">Confirmar nova senha</Label>
                <Input id="pwd2" data-testid="reset-pwd2-input" type={showPwd ? "text" : "password"} required minLength={6}
                  value={pwd2} onChange={(e) => setPwd2(e.target.value)} className="h-11 rounded-lg" placeholder="••••••••" />
              </div>

              <Button type="submit" data-testid="reset-submit-btn" disabled={loading}
                className="w-full h-11 rounded-lg bg-brand-turquoise hover:bg-[#3A9CA2] text-white font-semibold">
                {loading ? "Salvando…" : (<>Redefinir senha<ArrowRight className="ml-1.5 h-4 w-4" /></>)}
              </Button>
            </form>

            <div className="mt-6 pt-4 border-t border-border text-center">
              <Link to="/login" className="text-sm text-brand-turquoise hover:underline" data-testid="reset-back-login">
                Voltar para o login
              </Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
